import { useState, useEffect } from 'react'
import { Text, Primary, SvgIcon, TextLink } from '@cinch-labs/design-system'
import { StoreWithDistance } from '../../hooks/types'
import styles from './store-details-card.module.css'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { round } from 'lodash'
import { formatAddress } from '../../utils/store-card-details'
import { OpeningHoursDrawer } from '../opening-hours-drawer'

const METERS_TO_MILES_CONVERSION = 0.000_621_371
const toMiles = (meters: number) =>
  round(meters * METERS_TO_MILES_CONVERSION, 1)

interface StoreDetailsCardProps {
  store: StoreWithDistance
  isMinimized: boolean
  onExpand: () => void
  onSelectStore: () => void
}

export const StoreDetailsCard: React.FC<StoreDetailsCardProps> = ({
  store,
  isMinimized,
  onExpand,
  onSelectStore,
}) => {
  const { trackEvent } = useAnalytics()
  const [showOpeningHours, setShowOpeningHours] = useState(false)

  useEffect(() => {
    setShowOpeningHours(false)
  }, [store.store.id])

  const handleOpenOpeningHours = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowOpeningHours(true)

    trackEvent({
      eventName: `Toggle Store Info`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: 'opening_hours',
          label: store.store.name,
          type: 'map_view',
        },
      },
    })
  }

  return (
    <>
      <div
        className={`${styles.card} ${isMinimized ? styles.minimized : ''}`}
        onClick={isMinimized ? onExpand : undefined}
        style={{ cursor: isMinimized ? 'pointer' : 'default' }}
      >
        <div className={styles.cardContent}>
          <Text
            fontSizeFixed="m"
            fontWeight="bold"
            className={styles.storeName}
          >
            {store.store.displayName}
          </Text>

          <div
            className={`${styles.additionalContent} ${
              isMinimized ? styles.hidden : ''
            }`}
          >
            <Text fontSizeFixed="s" className={styles.address}>
              {formatAddress(store.store)}
            </Text>

            {store.distance !== 0 && (
              <Text fontSizeFixed="s" className={styles.distance}>
                {toMiles(store.distance)} miles away
              </Text>
            )}

            {store.store.openingHours && (
              <div className={styles.openingHoursLink}>
                <TextLink onClick={handleOpenOpeningHours}>
                  <Text
                    fontSizeFixed="s"
                    className={styles.openingHoursLinkText}
                  >
                    Opening hours <SvgIcon name="chevron-right" size={12} />
                  </Text>
                </TextLink>
              </div>
            )}
          </div>
        </div>

        <div
          className={`${styles.buttonContainer} ${
            isMinimized ? styles.hidden : ''
          }`}
        >
          <Primary
            onClick={() => {
              trackEvent({
                eventName: `Choose Store from Map`,
                type: TrackingEventTypes.ADOBE,
                data: {
                  event: {
                    name: EventNames.click,
                    category: 'cinch_servicing',
                    action: 'choose_store_from_map',
                    label: store.store.name,
                  },
                },
              })
              onSelectStore()
            }}
            size="large"
            classNames={{ button: styles.chooseButton }}
            aria-label={`Select ${store.store.name}`}
          >
            Select
          </Primary>
        </div>
      </div>

      {showOpeningHours && store.store.openingHours && (
        <OpeningHoursDrawer
          storeName={store.store.name}
          openingHours={store.store.openingHours}
          onClose={() => setShowOpeningHours(false)}
        />
      )}
    </>
  )
}
